// packages/web/src/lib/unread.ts — CV6b §4: THE APP REMEMBERS.
//
// A ping is for a person holding a wrench. When they put it down and open
// the app, the app has to remember what happened while they were not
// looking — otherwise a notification is a thing that happened to someone
// and left no trace anywhere, which is the same failure as a capture that
// vanishes.
//
// CV6a puts `unreadCount` on every conversation entry and gives the client
// `POST /api/comms/reads`. This module is the one place the WHOLE APP's
// unread total lives, because three surfaces need the same number and none
// of them owns it:
//
//   · the chat list (per thread) — reads its own entries,
//   · the Chat TAB (the total) — rendered by the shell, which never opens
//     the chat screen,
//   · the installed app ICON (the Badging API) — owned by nobody at all.
//
// A module store rather than a context because the shell and the screens
// are on opposite sides of the route switch, and a context spanning both
// would mean threading a provider through every branch of App.tsx to carry
// one integer.
//
// THE CAP TRAVELS WITH THE NUMBER. CV6a stops counting at 99 and says so
// (`unreadCountCapped`); a total that silently truncated would look exact
// and be wrong, so `capped` rides the total and the UI renders "99+".

export interface ThreadUnread {
  count: number;
  capped: boolean;
}

export interface UnreadTotal extends ThreadUnread {
  /** How many THREADS carry something new — the badge shows the message
   * count, but a caller that wants "3 conversations" has it here. */
  threads: number;
}

const counts = new Map<string, ThreadUnread>();
const listeners = new Set<(total: UnreadTotal) => void>();

/** The thread identity the whole app agrees on — the ROUTE's, and the same
 * one CV6a's `chat_reads` generated column enforces: `<lane>:<id>`. */
export function threadKey(lane: string, id: string): string {
  return `${lane}:${id}`;
}

export function unreadTotal(): UnreadTotal {
  let count = 0;
  let capped = false;
  let threads = 0;
  for (const entry of counts.values()) {
    if (entry.count <= 0) continue;
    count += entry.count;
    threads += 1;
    if (entry.capped) capped = true;
  }
  return { count, capped, threads };
}

/** What a badge SAYS. Never a bare truncated integer — "99+" is the honest
 * rendering of a count that stopped counting. */
export function unreadLabel(entry: ThreadUnread): string {
  return entry.capped ? `${entry.count}+` : String(entry.count);
}

function announce(): void {
  const total = unreadTotal();
  for (const listener of listeners) listener(total);
  applyAppBadge(total.count);
}

/**
 * THE INSTALLED ICON'S OWN BADGE. Absent support is a NO-OP, never a thrown
 * error: `setAppBadge` does not exist on iOS Safari at all today, exists but
 * is permission-gated in some installed contexts, and rejects rather than
 * throws in others. All three must leave the app working.
 */
export function applyAppBadge(count: number): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => {});
    else void nav.clearAppBadge?.()?.catch(() => {});
  } catch {
    /* an engine that throws synchronously is an engine without badges */
  }
}

/**
 * Replaces the WHOLE picture from one conversation-list read. Replace, not
 * merge: a thread that has dropped off the caller's list (a grant revoked,
 * a job closed) must stop contributing to the badge, and a merge would
 * leave it counting forever.
 */
export function publishThreadUnread(entries: { key: string; count: number; capped: boolean }[]): void {
  counts.clear();
  for (const entry of entries) {
    if (entry.count > 0) counts.set(entry.key, { count: entry.count, capped: entry.capped });
  }
  announce();
}

/** One thread just became read — the human opened it. Updates the list and
 * the badge without waiting for another list read, which is what makes
 * "opening a thread clears both" true immediately rather than eventually. */
export function clearThreadUnread(key: string): void {
  if (!counts.has(key)) return;
  counts.delete(key);
  announce();
}

export function subscribeUnread(listener: (total: UnreadTotal) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset. The store is a module singleton on purpose (see the
 * header); a suite that did not clear it would inherit the previous file's
 * counts. */
export function resetUnreadForTests(): void {
  counts.clear();
  listeners.clear();
}
